import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
    format,
    startOfMonth,
    endOfMonth,
    eachDayOfInterval,
    isSameMonth,
    isSameDay,
    addMonths,
    subMonths,
    startOfWeek,
    endOfWeek,
    addWeeks,
    subWeeks,
    isWeekend,
} from 'date-fns';
import { bookingStore } from '../stores/BookingStore';
import '../styles/Calendar.css';
import { toJS } from 'mobx';
import { getAllAvailabilityForDate, getAllBookingsForDate } from '../utils/functions';
import { BookingExcerpt } from './calendar/BookingExcerpt';
import { BookingView } from './calendar/BookingView';
import { TCalendarBooking } from '../types/types';
import { AvailabilityExcerpt } from './availability/AvailabilityExcerpt';

type ViewType = 'month' | 'week';

const emptyBooking: TCalendarBooking = {
    booking_id: 0,
    lodging_id: 0,
    guest_name: '',
    start_date: '',
    end_date: '',
    status: 'pending',
    nights: 0,
    guests: 0,
    guest_email: '',
    guest_phone: '',
    user_id: 0,
    establishment_id: 0,
    notes: '',
    pricing_structure_json: {}
};

export const Calendar: React.FC = observer(() => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [viewType, setViewType] = useState<ViewType>('month');
    const [layout, setLayout] = useState<'grid' | 'list'>('grid');
    const [showBooking, setShowBooking] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState<TCalendarBooking>();
    const [displayAvailability, setDisplayAvailability] = useState(true);

    // Calculate days based on view type
    const getViewDays = () => {
        if (viewType === 'month') {
            const monthStart = startOfMonth(currentDate);
            const monthEnd = endOfMonth(currentDate);
            const startDate = new Date(monthStart);
            startDate.setDate(startDate.getDate() - monthStart.getDay());

            return {
                days: eachDayOfInterval({
                    start: startDate,
                    end: new Date(monthEnd.getFullYear(), monthEnd.getMonth() + 1, 0),
                }),
                rangeStart: monthStart,
                rangeEnd: monthEnd,
            };
        } else {
            // Week view
            const weekStart = startOfWeek(currentDate);
            const weekEnd = endOfWeek(currentDate);

            return {
                days: eachDayOfInterval({
                    start: weekStart,
                    end: weekEnd,
                }),
                rangeStart: weekStart,
                rangeEnd: weekEnd,
            };
        }
    };

    const { days: daysInView, rangeStart, rangeEnd } = getViewDays();

    // Fetch calendar bookings when component mounts, date changes, or view type changes
    useEffect(() => {
        const fetchBookings = async () => {
            const fromDate = format(rangeStart, 'yyyy-MM-dd');
            const toDate = format(rangeEnd, 'yyyy-MM-dd');
            await bookingStore.fetchCalendarBookings(fromDate, toDate);
            await bookingStore.fetchAvailability(fromDate, toDate);
        };
        fetchBookings();
    }, [currentDate, viewType]);

    const handlePrevious = () => {
        if (viewType === 'month') {
            setCurrentDate(subMonths(currentDate, 1));
        } else {
            setCurrentDate(subWeeks(currentDate, 1));
        }
    };

    const handleNext = () => {
        if (viewType === 'month') {
            setCurrentDate(addMonths(currentDate, 1));
        } else {
            setCurrentDate(addWeeks(currentDate, 1));
        }
    };

    const handleToday = () => {
        setCurrentDate(new Date());
        setSelectedDate(new Date());
    };

    const selectBookingToView = (booking: TCalendarBooking) => {
        if (layout === 'list' && !booking.booking_id) {
            const today = new Date();
            setSelectedDate(today >= rangeStart ? today : rangeStart);
        }
        setShowBooking(true);
        setSelectedBooking(booking);

    }

    const clearBookingToView = () => {
        setShowBooking(false);
        setSelectedBooking(undefined);
    }

    const copyOfBookings = toJS(bookingStore.calendarBookings);
    const copyOfAvailability = toJS(bookingStore.calendarAvailability);
    return (
        <div className="calendar">

            {showBooking && selectedBooking ?
                <BookingView booking={selectedBooking} selectBookingToView={selectBookingToView} clearBookingToView={clearBookingToView}
                    getViewDays={getViewDays} selectedDate={selectedDate} />
                :

                <>
                    <div className="calendar-header mobile-hide">
                        <button onClick={handlePrevious} className="nav-button" title="Previous">
                            <i className="fa-solid fa-chevron-left"></i>
                        </button>
                        <button onClick={handleToday} className="nav-button">
                            Today
                        </button>
                        <button onClick={() => selectBookingToView(emptyBooking)}
                            className="nav-button new-booking-button"
                        >
                            <i className="fa-regular fa-calendar-plus"></i>
                        </button>
                        <div className="radio-group">
                            <label>
                                <input
                                    type="radio"
                                    name="availability"
                                    value="show"
                                    checked={displayAvailability}
                                    onChange={() => setDisplayAvailability(true)}
                                />
                                <i className="fa-solid fa-eye"></i> Availability
                            </label>
                            <label>
                                <input
                                    type="radio"
                                    name="availability"
                                    value="hide"
                                    checked={!displayAvailability}
                                    onChange={() => setDisplayAvailability(false)}
                                />
                                <i className="fa-solid fa-eye-slash"></i> Availability
                            </label>
                        </div>
                        <div className="calendar-title">
                            {viewType === 'month' ? (
                                <h2>{format(currentDate, 'MMMM yyyy')}</h2>
                            ) : (
                                <h2>{format(rangeStart, 'MMM d')} - {format(rangeEnd, 'MMM d, yyyy')}</h2>
                            )}
                        </div>
                        <div className="radio-group">
                            <label>
                                <input
                                    type="radio"
                                    name="layout"
                                    value="grid"
                                    checked={layout === 'grid'}
                                    onChange={() => setLayout('grid')}
                                />
                                <i className="fa-solid fa-table-cells-large"></i> Grid
                            </label>
                            <label>
                                <input
                                    type="radio"
                                    name="layout"
                                    value="list"
                                    checked={layout === 'list'}
                                    onChange={() => setLayout('list')}
                                />
                                <i className="fa-solid fa-list"></i> List
                            </label>
                        </div>
                        <div className="radio-group">
                            <label>
                                <input
                                    type="radio"
                                    name="viewType"
                                    value="month"
                                    checked={viewType === 'month'}
                                    onChange={() => setViewType('month')}
                                />
                                <i className="fa-regular fa-calendar"></i> Month
                            </label>
                            <label>
                                <input
                                    type="radio"
                                    name="viewType"
                                    value="week"
                                    checked={viewType === 'week'}
                                    onChange={() => setViewType('week')}
                                />
                                <i className="fa-solid fa-calendar-week"></i> Week
                            </label>
                        </div>
                        
                        <button onClick={handleNext} className="nav-button" title="Next">
                            <i className="fa-solid fa-chevron-right"></i>
                        </button>
                    </div>

                    <div className="mobile-display-w768 text-center pb-2">
                        {viewType === 'month' ? (
                            <h4>{format(currentDate, 'MMMM yyyy')}</h4>
                        ) : (
                            <h4>{format(rangeStart, 'MMM d')} - {format(rangeEnd, 'MMM d, yyyy')}</h4>
                        )}
                    </div>

                    {layout === 'list' ?
                        <>
                            <table className="table table-sm table-striped table-hover">
                                <tbody>
                                    <tr className='small'>
                                        <td>#</td>
                                        <td>Site</td>
                                        <td>Guest</td>
                                        <td>Contact</td>
                                        <td>Check In</td>
                                        <td>Check Out</td>
                                        <td>Nights</td>
                                        <td>Guests</td>
                                        <td>Status</td>
                                        <td>Paid</td>
                                        <td>Total Price</td>
                                    </tr>
                                    {copyOfBookings.map((booking) => (
                                        <tr
                                            className='small'
                                            key={booking.booking_id}
                                            onClick={() => selectBookingToView(booking)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <td>#{booking.booking_id}</td>
                                            <td>{booking.lodging_name}</td>
                                            <td>{booking.guest_name}</td>
                                            <td>{booking.guest_phone}</td>
                                            <td>{booking.start_date}</td>
                                            <td>{booking.end_date}</td>
                                            <td>{booking.nights}</td>
                                            <td>{booking.guests}</td>
                                            <td>{booking.status}
                                            </td>
                                            <td>{booking.paid ? 'Paid' : 'Unpaid'}
                                            </td>
                                            <td>
                                                R{((booking.pricing_structure_json?.total_price as number) || 0).toFixed(2)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                        :
                        <>
                            <div className="calendar-header mobile-display-w768">
                                <button onClick={handlePrevious} className="nav-button" title="Previous">
                                    <i className="fa-solid fa-chevron-left"></i>
                                </button>

                                <button onClick={() => selectBookingToView(emptyBooking)} className="nav-button new-booking-button">
                                    <i className="fa-regular fa-calendar-plus"></i>
                                </button>
                                <button onClick={handleToday} className="nav-button">
                                    Today
                                </button>
                                <button onClick={handleNext} className="nav-button" title="Next">
                                    <i className="fa-solid fa-chevron-right"></i>
                                </button>
                            </div>

                            <div className="weekdays mobile-hide">
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                                    <div key={day} className="weekday">
                                        {day}
                                    </div>
                                ))}
                            </div>
                            <div className={`calendar-grid ${viewType}`}>
                                {daysInView.map((day) => {
                                    const isCurrentMonth = isSameMonth(day, currentDate);
                                    const isSelected = selectedDate && isSameDay(day, selectedDate);
                                    const isToday = isSameDay(day, new Date());
                                    const isInRange = viewType === 'week' || isCurrentMonth;
                                    const dayNr = format(day, 'd')
                                    const yyyymmdd = format(day, 'yyyy-MM-dd');
                                    const naweek = isWeekend(day);
                                    const dayName = format(day, 'EEE');
                                    const dayBookings = getAllBookingsForDate(copyOfBookings, yyyymmdd);
                                    const dayAvailability = getAllAvailabilityForDate(copyOfAvailability, yyyymmdd);
                                    return (
                                        <button
                                            key={day.toISOString()}
                                            className={`calendar-day ${!isInRange ? 'other-month' : ''} ${isSelected ? 'selected' : ''
                                                } ${isToday ? 'today' : ''}  ${naweek ? 'naweek' : ''}`}
                                            onClick={() => setSelectedDate(day)}
                                        >
                                            <div className="day-number">
                                                <span className='mobile-hide'>{dayNr}</span>
                                                <span className='mobile-display-w768'><strong>{dayName}</strong><br />{yyyymmdd}</span>
                                                <div className='day-info'>
                                                    <div className='container'>
                                                        {dayAvailability.length > 0 ? 
                                                        <div className='row justify-content-center'>
                                                            {dayBookings.map(b => {
                                                                return <BookingExcerpt key={b.booking_id} booking={b} selectBookingToView={selectBookingToView} viewType={viewType} />
                                                            }
                                                            )}
                                                        </div> : null}

                                                        {displayAvailability && (
                                                            <div className='row justify-content-center'>
                                                                {dayAvailability.map(a => {
                                                                    return <AvailabilityExcerpt key={a.lodging_id} availability={a} />
                                                                }
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </>
                    }
                </>
            }
        </div>
    );
});
